/// <reference path="../typings/index.d.ts" />
/**
 * TransferCoins.ts — Transferência entre contas (beta).
 *
 * - Contas A/B cadastradas ou temporárias (lista única em localStorage, chave lógica por e-mail).
 * - Ações ao plugin por socket (ACAO_TRANSFER_* 46–55).
 * - check-balance via plugin; registo pós-transferência enfileirado no plugin (lowdb → API).
 */

namespace App {

  interface IContaTemporaria {
    id: string;
    email: string;
    senhaOfuscada: string;
    backup?: string;
  }

  const FSW_TC_KEY = 'FSW_TC_BUYER_KEY_2024';
  /** Lista única de credenciais fora do cadastro (vendedor e comprador). */
  const LS_CONTAS_OFF = 'fsw_transfer_contas_off_cadastro';
  /** Legado — migrado automaticamente para LS_CONTAS_OFF. */
  const LS_CONTAS_TEMP_LEGACY = 'fsw_transfer_contas_buyer_temp';
  const SENHA_LS_PREFIX = 'b64u1:';

  function normalizarEmail(s: string): string {
    return (s || '').trim().toLowerCase();
  }

  function idPorEmailNormalizado(norm: string): string {
    return 'em:' + norm;
  }

  function desofuscarSenhaLegadoXor(s: string): string {
    if (!s || typeof s !== 'string') return '';
    try {
      const d = atob(s);
      var chars: string[] = [];
      for (var i = 0; i < d.length; i++) {
        var c = d.charAt(i);
        chars.push(String.fromCharCode(c.charCodeAt(0) ^ FSW_TC_KEY.charCodeAt(i % FSW_TC_KEY.length)));
      }
      return chars.join('');
    } catch (e) {
      return s;
    }
  }

  function codificarSenhaParaArmazenar(s: string): string {
    if (s == null) return '';
    try {
      return SENHA_LS_PREFIX + btoa(unescape(encodeURIComponent(s)));
    } catch (e) {
      return SENHA_LS_PREFIX + btoa(s);
    }
  }

  function decodificarSenhaArmazenada(s: string): string {
    if (s == null || typeof s !== 'string') return '';
    if (s.indexOf(SENHA_LS_PREFIX) === 0) {
      try {
        var b64 = s.slice(SENHA_LS_PREFIX.length);
        return decodeURIComponent(escape(atob(b64)));
      } catch (e) {
        return '';
      }
    }
    return desofuscarSenhaLegadoXor(s);
  }

  /** Taxa EA sobre o valor de venda (conta A). */
  const FSW_TAXA_EA_VENDA = 0.05;
  /** Desconto “lado B / ref.” no cálculo do valor transferido líquido (5% sobre o preço de referência). */
  const FSW_TAXA_REF_MERCADO = 0.05;
  const FSW_PERDA_VENDA_PADRAO = 2;
  const FSW_PERDA_VENDA_MAX = 15;
  const FSW_BIN_STEP = 50;
  const FSW_BIN_MIN = 150;

  /** Sons após o resultado da ação «Transferir» (`public/audios/`). */
  const FSW_AUDIO_TRANSFER_SUCESSO = '/audios/moeda-transfer.mp3';
  const FSW_AUDIO_TRANSFER_FALHA = '/audios/2.mp3';
  const FSW_AUTO_TAXA_EA = 0.05;
  const FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML = '<i class="fas fa-search-dollar mr-1"></i>Preparar contas e calcular transferência';

  function gerarClientAttemptId(): string {
    try {
      var c: any = typeof crypto !== 'undefined' ? crypto : null;
      if (c && typeof c.randomUUID === 'function') {
        return c.randomUUID().replace(/-/g, '');
      }
    } catch (e) { /* ignore */ }
    return 't' + Date.now().toString(36) + Math.random().toString(36).slice(2, 14);
  }

  export class TransferCoins {

    private modal = $('#modalTransferConta');
    private elEmailA = this.modal.find('.transfer-conta-a-email');
    private elEmailB = this.modal.find('.transfer-conta-b-email');
    private elStatusA = this.modal.find('.transfer-conta-a-status');
    private elStatusB = this.modal.find('.transfer-conta-b-status');
    private elBtnPrepararA = this.modal.find('.btn-preparar-conta-a');
    private elBtnPrepararB = this.modal.find('.btn-preparar-conta-b');
    private elBtnSwap = this.modal.find('.btn-swap-contas');
    private elSelectJogador = this.modal.find('.transfer-select-jogador');
    private elInputPreco = this.modal.find('.transfer-input-preco');
    private elPrecoMercado = this.modal.find('.transfer-preco-mercado');
    private elInputPerdaVenda = this.modal.find('.transfer-input-perda-venda');
    private elBinSugerido = this.modal.find('.transfer-bin-sugerido');
    private elTaxValor = this.modal.find('.transfer-tax-valor');
    private elTaxRefMercado = this.modal.find('.transfer-tax-ref-mercado');
    private elTaxRecebe = this.modal.find('.transfer-tax-recebe');
    private elValorTransferido = this.modal.find('.transfer-valor-transferido');
    private elFswCustoEur = this.modal.find('.transfer-fsw-custo-eur');
    private elLogLines = this.modal.find('.transfer-log-lines');
    private elBtnExecutar = this.modal.find('.btn-transfer-executar');
    private elBtnReset = this.modal.find('.btn-transfer-reset');
    private elGeralStatus = this.modal.find('.transfer-geral-status');
    private elLatency = this.modal.find('.transfer-latency');
    private elSessionInfo = this.modal.find('.transfer-session-info');
    private elTabLinks = this.modal.find('.transfer-tab-link');
    private elManualFooter = this.modal.find('.transfer-manual-footer');
    private elAutoCoins = this.modal.find('.transfer-auto-coins');
    private elAutoCarta = this.modal.find('.transfer-auto-carta');
    private elAutoMargem = this.modal.find('.transfer-auto-margem');
    private elAutoBtnCalcular = this.modal.find('.btn-transfer-auto-calcular');
    private elAutoBtnConfirmar = this.modal.find('.btn-transfer-auto-confirmar');
    private elAutoBtnCancelar = this.modal.find('.btn-transfer-auto-cancelar');
    private elAutoSteps = this.modal.find('.transfer-auto-steps');
    private elAutoMetricas = this.modal.find('.transfer-auto-metricas');

    private elACrudActions = this.modal.find('.transfer-a-crud-actions');
    private elADropdown = this.modal.find('.transfer-a-dropdown');
    private elADropdownLista = this.modal.find('.transfer-a-dropdown-lista');
    private elADropdownVazio = this.modal.find('.transfer-a-dropdown-vazio');
    private elAForm = this.modal.find('.transfer-a-form');
    private elAFormEmail = this.modal.find('.transfer-a-form-email');
    private elAFormSenha = this.modal.find('.transfer-a-form-senha');
    private elAFormBackup = this.modal.find('.transfer-a-form-backup');
    private elASearchWrap = this.modal.find('.transfer-a-cred-search-wrap');
    private elASearch = this.modal.find('.transfer-a-cred-search');

    private elBCrudActions = this.modal.find('.transfer-b-crud-actions');
    private elBDropdown = this.modal.find('.transfer-b-dropdown');
    private elBDropdownLista = this.modal.find('.transfer-b-dropdown-lista');
    private elBDropdownVazio = this.modal.find('.transfer-b-dropdown-vazio');
    private elBForm = this.modal.find('.transfer-b-form');
    private elBFormEmail = this.modal.find('.transfer-b-form-email');
    private elBFormSenha = this.modal.find('.transfer-b-form-senha');
    private elBFormBackup = this.modal.find('.transfer-b-form-backup');
    private elBSearchWrap = this.modal.find('.transfer-b-cred-search-wrap');
    private elBSearch = this.modal.find('.transfer-b-cred-search');

    private contaA: IConta = null;
    private contaB: IConta = null;
    private contaATemp: IContaTemporaria = null;
    private contaBTemp: IContaTemporaria = null;
    private modoContaATemp: boolean = false;
    private modoContaBTemp: boolean = false;
    private contaAPronta: boolean = false;
    private contaBPronta: boolean = false;
    private executando: boolean = false;
    private precoMercadoSelecionado: number = 0;
    private intervalLatencia: any = null;
    private intervalHeartbeat: any = null;
    private editandoContaTempIdA: string = null;
    private editandoContaTempIdB: string = null;
    private idExtensao: string = null;
    private modoTransferSelecionado: 'manual' | 'automatica' = 'manual';
    private autoOperationId: string = null;
    private autoCalculoAtual: any = null;
    private autoExecutando: boolean = false;
    private autoAudioTransferManualTocado: boolean = false;

    private comunicacao: Comunicacao;
    private arrayContas: IConta[];

    constructor(comunicacao: Comunicacao, arrayContas: IConta[], idExtensao?: string) {
      this.comunicacao = comunicacao;
      this.arrayContas = arrayContas;
      this.idExtensao = idExtensao || ($('#idExtensao').val() as string) || null;
      this.inicializarEventos();
      this.inicializarSelectJogadorAuto();
    }

    public setComunicacao(comunicacao: Comunicacao) {
      this.comunicacao = comunicacao;
    }

    public setArrayContas(contas: IConta[]) {
      this.arrayContas = contas;
    }

    private getIdContaAPlugin(): number | null {
      if (this.contaA) return this.contaA.id;
      if (this.contaATemp && (this.contaATemp as any).serverId != null) {
        return Number((this.contaATemp as any).serverId);
      }
      return null;
    }

    private getIdContaBPlugin(): number | null {
      if (this.contaB) return this.contaB.id;
      if (this.contaBTemp && (this.contaBTemp as any).serverId != null) {
        return Number((this.contaBTemp as any).serverId);
      }
      return null;
    }

    /**
     * Bloqueia só PC ↔ consola (PlayStation ou Xbox). PSN ↔ Xbox é permitido.
     * Usa `id_plataforma` (1/4 PlayStation, 2 Xbox, 3 PC na base típica) e, se faltar, `dadosEa.plataforma`.
     */
    private categoriaPlataformaContaWeb(conta: any): 'pc' | 'psn' | 'xbl' | null {
      var id = conta.id_plataforma != null ? Number(conta.id_plataforma) : NaN;
      if (!isNaN(id)) {
        if (id === 3) return 'pc';
        if (id === 2) return 'xbl';
        if (id === 1 || id === 4) return 'psn';
      }
      var d = conta && conta.dadosEa && conta.dadosEa.plataforma;
      if (!d) return null;
      var u = String(d).trim().toUpperCase();
      if (u === 'PC') return 'pc';
      if (u === 'PSN' || u === 'PS4' || u === 'PS5' || u === 'PS3' || u === 'PS') return 'psn';
      if (u === 'XBL' || u === 'XBOX' || u === 'XBOX_ONE' || u === 'XBO' || u === 'XBOXONE') return 'xbl';
      if (u.indexOf('PS') === 0) return 'psn';
      if (u.indexOf('XBOX') >= 0 || u.indexOf('XBL') >= 0) return 'xbl';
      return null;
    }

    private contasSelecionadasComPlataformaDiferente(): boolean {
      if (!this.contaA || !this.contaB) return false;
      var ca = this.categoriaPlataformaContaWeb(this.contaA as any);
      var cb = this.categoriaPlataformaContaWeb(this.contaB as any);
      if (ca == null || cb == null) return false;
      if (ca === cb) return false;
      if ((ca === 'psn' && cb === 'xbl') || (ca === 'xbl' && cb === 'psn')) return false;
      return true;
    }

    private enviarRemoverPerfilTemp(email: string) {
      var e = (email || '').trim();
      if (!e) return;
      this.comunicacao.enviar(Util.ACAO_TRANSFER_REMOVER_PERFIL_TEMP, { email: e }, () => {});
    }

    // ─── Abrir modal ─────────────────────────────────────────────────────────

    public abrirModal(idA?: number, idB?: number) {
      this.reset(true);
      var contaA: IConta = null;
      var contaB: IConta = null;
      if (idA != null) {
        for (var i = 0; i < this.arrayContas.length; i++) {
          if (this.arrayContas[i].id == idA) contaA = this.arrayContas[i];
        }
      }
      if (idB != null) {
        for (var j = 0; j < this.arrayContas.length; j++) {
          if (this.arrayContas[j].id == idB) contaB = this.arrayContas[j];
        }
      }

      this.contaA = contaA;
      this.contaB = contaB;
      this.contaATemp = null;
      this.contaBTemp = null;
      this.modoContaATemp = !contaA;
      this.modoContaBTemp = !contaB;

      this.renderizarContas();
      this.aplicarModoUiCredenciais();
      this.modal.modal('show');
      this.sincronizarAbaTransferAtual();
      this.iniciarAtualizacaoLatencia();
    }

    private aplicarModoUiCredenciais() {
      if (this.modoContaATemp) {
        this.elACrudActions.show();
        this.elASearchWrap.show();
        this.elEmailA.css('cursor', 'pointer');
      } else {
        this.elACrudActions.hide();
        this.elASearchWrap.hide();
        this.elEmailA.css('cursor', 'default');
      }
      if (this.modoContaBTemp) {
        this.elBCrudActions.show();
        this.elBSearchWrap.show();
        this.elEmailB.css('cursor', 'pointer');
      } else {
        this.elBCrudActions.hide();
        this.elBSearchWrap.hide();
        this.elEmailB.css('cursor', 'default');
      }
    }

    public onTransferStatus(data: { msg: string; tipo: 'info' | 'sucesso' | 'erro'; modo?: string; etapa?: string; metricas?: any; final?: boolean }) {
      this.adicionarLog(data.msg, data.tipo);
      if (data && data.modo === 'automatico') {
        this.aplicarEtapaAuto(data.etapa || null, data.tipo);
        if (data.metricas) this.renderizarMetricasAuto(data.metricas);
        if (data.etapa === 'transfer_concluida' && (data.tipo === 'sucesso' || data.tipo === 'erro') && !this.autoAudioTransferManualTocado) {
          this.autoAudioTransferManualTocado = true;
          this.tocarAudioResultadoTransferencia(data.tipo === 'sucesso');
        }
        if (data.final || data.tipo === 'erro') {
          this.autoExecutando = false;
          this.elAutoBtnCalcular.prop('disabled', false).html(FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML);
          this.elAutoBtnConfirmar.prop('disabled', !this.autoOperationId || data.tipo !== 'erro');
          this.elAutoBtnCancelar.prop('disabled', true);
          this.atualizarGeralStatus(data.tipo === 'erro' ? 'Falhou' : 'Concluído', data.tipo);
        }
        return;
      }
      if (data.tipo === 'sucesso' || data.tipo === 'erro') {
        if (this.autoExecutando && data.tipo === 'sucesso' && !this.autoAudioTransferManualTocado && String(data.msg || '').indexOf('TradeId') >= 0) {
          this.autoAudioTransferManualTocado = true;
          this.tocarAudioResultadoTransferencia(true);
        }
        this.executando = false;
        this.atualizarGeralStatus(data.tipo === 'sucesso' ? 'Concluído' : 'Falhou', data.tipo);
        this.elBtnExecutar.prop('disabled', false).html('<i class="fas fa-exchange-alt mr-1"></i>Transferir');
      }
    }

    public onTransferJogadores(data: { itens: any[] }) {
      this.popularSelectJogadores(data.itens ? data.itens : []);
    }

    private alternarAbaTransfer(tab: string, atualizarStatus: boolean = true) {
      this.modoTransferSelecionado = tab === 'automatica' ? 'automatica' : 'manual';
      var modoAutomatico = this.modoTransferSelecionado === 'automatica';
      this.elManualFooter.toggle(this.modoTransferSelecionado === 'manual');
      this.elBtnPrepararA.toggle(!modoAutomatico);
      this.elBtnPrepararB.toggle(!modoAutomatico);
      if (atualizarStatus) {
        this.atualizarGeralStatus(this.modoTransferSelecionado === 'manual' ? 'Modo manual' : 'Modo SemiAutomático', 'info');
      }
    }

    private sincronizarAbaTransferAtual() {
      var tab = this.modal.find('.transfer-tab-link.active').data('transferTab') || this.modoTransferSelecionado || 'manual';
      this.alternarAbaTransfer(tab, false);
    }

    private formatCoinsAuto(valor: any): string {
      var n = Number(valor);
      if (!isFinite(n) || n <= 0) return '—';
      return Math.floor(n).toLocaleString('pt-BR');
    }

    private parsePercentualRevendaAuto(raw: string): number {
      var limpo = String(raw || '').replace(',', '.').replace(/[^0-9.\-]/g, '');
      var n = parseFloat(limpo);
      return isFinite(n) ? n : 0;
    }

    private parseValorFixoRevendaAuto(raw: string): number {
      var limpo = String(raw || '').replace(/[^\d]/g, '');
      var n = Number(limpo);
      return isFinite(n) ? Math.floor(n) : 0;
    }

    private obterLimitesRevendaAuto(calculo: any): any {
      var min = Math.floor(Number(calculo && calculo.precoMinimo ? calculo.precoMinimo : 0));
      var max = Math.floor(Number(calculo && calculo.precoMaximo ? calculo.precoMaximo : 0));
      return min > 0 && max > 0 && min <= max ? { min: min, max: max } : null;
    }

    private corrigirValorFixoRevendaAuto(valor: number, limites: any = null): number {
      if (!valor || valor <= 0 || !isFinite(valor)) return 0;
      var ajustado = Math.floor(Number(Util.tratarValorDeFiltro(Math.floor(valor))) || 0);
      if (ajustado > 0 && ajustado < FSW_BIN_MIN) ajustado = FSW_BIN_MIN;
      if (limites) {
        if (limites.min > 0 && ajustado < limites.min) ajustado = limites.min;
        if (limites.max > 0 && ajustado > limites.max) ajustado = limites.max;
      }
      return ajustado;
    }

    private obterConfigRevendaAuto(limites: any = null): any {
      var entrada = String(this.elAutoMargem.val() || '').trim();
      if (!entrada) entrada = '1';
      var lower = entrada.toLowerCase();
      var normalizadoSeparador = lower.replace(/\s/g, '').replace(/[^\d.,]/g, '');
      var percentual = this.parsePercentualRevendaAuto(entrada);
      var valorFixoOriginal = this.parseValorFixoRevendaAuto(entrada);
      var temPercentual = lower.indexOf('%') >= 0;
      var temCoins = lower.indexOf('coin') >= 0;
      var pareceMilhar = /^\d{1,3}([.,]\d{3})+$/.test(normalizadoSeparador);
      var modoFixo = !temPercentual && (temCoins || pareceMilhar || percentual > 30 || valorFixoOriginal > 30);
      if (modoFixo) {
        return {
          modoRevenda: 'fixo',
          entradaRevenda: entrada,
          margemPct: 0,
          valorRevendaInformado: valorFixoOriginal || null,
          revendaBUsuario: this.corrigirValorFixoRevendaAuto(valorFixoOriginal, limites),
        };
      }
      var margem = Math.max(0, Math.min(30, percentual));
      return {
        modoRevenda: 'percentual',
        entradaRevenda: entrada,
        margemPct: margem,
        valorRevendaInformado: margem,
        revendaBUsuario: null,
      };
    }

    private ajustarPrecoEaAuto(valor: number, direcao: 'floor' | 'ceil' | 'round' = 'floor'): number {
      var v = Math.max(0, Math.floor(Number(valor) || 0));
      var passo = Number(Util.obterIncrementoValorFiltro(v)) || FSW_BIN_STEP;
      if (direcao === 'ceil') return Math.ceil(v / passo) * passo;
      if (direcao === 'round') return Math.round(v / passo) * passo;
      return Math.floor(v / passo) * passo;
    }

    private decrementarPrecoEaAuto(valor: number): number {
      var v = Math.max(0, Math.floor(Number(valor) || 0));
      var passo = Number(Util.obterIncrementoValorFiltro(v)) || FSW_BIN_STEP;
      return Math.max(FSW_BIN_MIN, v - passo);
    }

    private calcularTransferAutoLocal(base: any, configRevenda: any): any {
      var limites = this.obterLimitesRevendaAuto(base);
      var precoMercado = Math.floor(Number(base && base.precoEA ? base.precoEA : 0));
      var objetivo = Math.floor(Number(this.elAutoCoins.val()) || 0);
      var margem = Math.max(0, Math.min(30, Number(configRevenda && configRevenda.margemPct ? configRevenda.margemPct : 0)));
      if (!precoMercado || !limites) return base;
      if (!objetivo || objetivo <= 0) {
        return $.extend({}, base, {
          viavel: false,
          motivo: 'Coins objetivo inválidas.',
          coinsObjetivo: objetivo,
          precoListagemA: null,
          startingBidA: null,
          coinsEstimadas: null,
        });
      }

      var modoRevenda = configRevenda && configRevenda.modoRevenda === 'fixo' ? 'fixo' : 'percentual';
      var valorRevendaInformado = modoRevenda === 'fixo' ? Number(configRevenda.revendaBUsuario || 0) : margem;
      var revendaB = 0;
      var revendaCorrigida = false;
      var motivoRevendaCorrigida = null;

      if (modoRevenda === 'fixo') {
        var original = Math.floor(Number(configRevenda.valorRevendaInformado || configRevenda.revendaBUsuario || 0));
        var porStep = this.corrigirValorFixoRevendaAuto(original, null);
        revendaB = this.corrigirValorFixoRevendaAuto(original, limites);
        revendaCorrigida = original !== revendaB;
        if (original !== porStep) motivoRevendaCorrigida = 'Valor ajustado ao step EA.';
        if (porStep !== revendaB) motivoRevendaCorrigida = 'Valor ajustado ao limite da carta.';
        valorRevendaInformado = original || valorRevendaInformado;
      } else {
        revendaB = this.ajustarPrecoEaAuto(precoMercado * (1 - (margem / 100)), 'floor');
        var revendaAntesLimite = revendaB;
        revendaB = Math.max(limites.min, Math.min(limites.max, revendaB));
        revendaCorrigida = revendaAntesLimite !== revendaB;
        if (revendaCorrigida) motivoRevendaCorrigida = 'Valor ajustado ao limite da carta.';
      }

      var taxaEa = Math.floor(revendaB * FSW_AUTO_TAXA_EA);
      var liquidoB = Math.floor(revendaB * (1 - FSW_AUTO_TAXA_EA));
      var tetoListagem = liquidoB - objetivo;
      var precoListagemA = tetoListagem >= limites.min ? this.ajustarPrecoEaAuto(Math.min(tetoListagem, limites.max), 'floor') : null;
      var coinsEstimadas = precoListagemA ? liquidoB - precoListagemA : null;
      var viavel = !!precoListagemA
        && precoListagemA >= limites.min
        && precoListagemA <= limites.max
        && revendaB >= limites.min
        && revendaB <= limites.max
        && coinsEstimadas >= objetivo;

      return {
        viavel: viavel,
        motivo: viavel ? null : 'Cálculo inviável: Conta B receberia menos do que o objetivo.',
        precoEA: precoMercado,
        precoMinimo: limites.min,
        precoMaximo: limites.max,
        itemPriceLimits: base.itemPriceLimits || null,
        modoRevenda: modoRevenda,
        margemPct: margem,
        valorRevendaInformado: valorRevendaInformado,
        revendaCorrigida: revendaCorrigida,
        motivoRevendaCorrigida: motivoRevendaCorrigida,
        coinsObjetivo: objetivo,
        revendaB: revendaB,
        startingBidB: this.decrementarPrecoEaAuto(revendaB),
        taxaEa: taxaEa,
        liquidoB: liquidoB,
        precoListagemA: precoListagemA,
        startingBidA: precoListagemA ? this.decrementarPrecoEaAuto(precoListagemA) : null,
        coinsEstimadas: coinsEstimadas,
        statusVenda: base.statusVenda || 'Aguardando',
      };
    }

    private aplicarRevendaCorrigidaAoCampoAuto(calculo: any) {
      if (calculo && calculo.modoRevenda === 'fixo' && calculo.revendaB > 0) {
        this.elAutoMargem.val(String(Math.floor(Number(calculo.revendaB))));
      }
    }

    private atualizarBotaoConfirmarAuto() {
      this.elAutoBtnConfirmar.prop('disabled', this.autoExecutando || !this.autoOperationId || !this.autoCalculoAtual || this.autoCalculoAtual.viavel !== true);
    }

    private recalcularTransferAutoComRevendaAtual(corrigirCampo: boolean = false) {
      var limites = this.obterLimitesRevendaAuto(this.autoCalculoAtual);
      var configRevenda = this.obterConfigRevendaAuto(limites);
      if (corrigirCampo && configRevenda.modoRevenda === 'fixo' && configRevenda.revendaBUsuario > 0) {
        this.elAutoMargem.val(String(configRevenda.revendaBUsuario));
      }
      if (!this.autoCalculoAtual || !this.autoCalculoAtual.precoEA || !limites) return;
      var calculo = this.calcularTransferAutoLocal(this.autoCalculoAtual, configRevenda);
      this.autoCalculoAtual = $.extend({}, this.autoCalculoAtual, calculo);
      this.renderizarMetricasAuto(this.autoCalculoAtual);
      this.atualizarBotaoConfirmarAuto();
    }

    /**
     * Busca no MESMO catalogo que serve o solver (storage/app/concepts, via
     * /api/jogador/concept-players). Antes baixava public/concepts.json inteiro (1,8 MB gzip,
     * 25 MB em memoria) so para filtrar no cliente — e obrigava a manter dois arquivos de
     * conceito em lugares diferentes.
     */
    private buscarConceitosAuto(termo: string): any {
      return $.getJSON('/api/jogador/concept-players', {
        q: termo,
        ordem: 'rating_desc',
        perPage: 30,
        page: 1,
      }).then((resposta: any) => {
        var lista = resposta && $.isArray(resposta.data) ? resposta.data : [];
        var resultados: any[] = [];
        for (var i = 0; i < lista.length; i++) resultados.push(this.montarItemJogadorAuto(lista[i]));
        return resultados;
      });
    }

    private primeiroValorAuto(obj: any, campos: string[]): any {
      for (var i = 0; i < campos.length; i++) {
        var campo = campos[i];
        if (obj && obj[campo] !== null && typeof obj[campo] !== 'undefined' && obj[campo] !== '') return obj[campo];
      }
      return '';
    }

    private normalizarPosicaoAuto(valor: any): string {
      if (valor === null || typeof valor === 'undefined' || valor === '') return '';
      var texto = String(valor);
      if (isNaN(Number(texto))) return texto;
      var mapa: any = {
        0: 'GK', 1: 'SW', 2: 'RWB', 3: 'RB', 4: 'RCB', 5: 'CB', 6: 'LCB', 7: 'LB', 8: 'LWB',
        9: 'RDM', 10: 'CDM', 11: 'LDM', 12: 'RM', 13: 'RCM', 14: 'CM', 15: 'LCM', 16: 'LM',
        17: 'RAM', 18: 'CAM', 19: 'LAM', 20: 'RF', 21: 'CF', 22: 'LF', 23: 'RW', 24: 'RS',
        25: 'ST', 26: 'LS', 27: 'LW',
      };
      return mapa[Number(texto)] || texto;
    }

    private montarUrlFutnextAuto(tipo: string, id: any): string {
      if (!id) return '';
      var pathTipo: any = {
        player: 'player',
        nation: 'nation',
        club: 'club',
        league: 'league',
        rarity: 'rarity',
      };
      var nomeArquivo = tipo === 'rarity' ? 'e_' + id + '_0' : id;
      return 'https://cdn.futnext.com/' + pathTipo[tipo] + '/' + nomeArquivo + '.png';
    }

    private montarItemJogadorAuto(jogador: any): any {
      var definitionId = this.primeiroValorAuto(jogador, ['definitionId', 'resourceId', 'id']);
      var assetId = this.primeiroValorAuto(jogador, ['assetId', 'assetid', '_assetId', 'resourceId', 'definitionId', 'id']);
      var nome = jogador.name || [jogador.firstName, jogador.lastName].filter((v: any) => !!v).join(' ') || 'Jogador';
      var rating = this.primeiroValorAuto(jogador, ['rating', 'r']);
      var posicao = this.normalizarPosicaoAuto(this.primeiroValorAuto(jogador, ['preferredPosition', 'preferredPositionName', 'position']));
      var rareflag = this.primeiroValorAuto(jogador, ['rareflag', 'rarityId', 'cardsubtypeid']);
      var ratingTier = this.primeiroValorAuto(jogador, ['ratingTier', 'tier']);
      var cardType = jogador.cardType || (rareflag !== '' ? 'Rareflag ' + rareflag : '-');
      var teamId = this.primeiroValorAuto(jogador, ['teamId', 'teamid', 'clubId', 'normalizeClubId']);
      var nationId = this.primeiroValorAuto(jogador, ['nationId', 'nation', 'nationid']);
      var leagueId = this.primeiroValorAuto(jogador, ['leagueId', 'leagueid']);
      var teamName = this.primeiroValorAuto(jogador, ['teamName', 'clubName', 'club_name', 'team_name', 'nomeClube', 'nome_clube']) || 'Clube';
      var nationName = this.primeiroValorAuto(jogador, ['nationName', 'nation_name', 'countryName', 'country_name', 'nationalityName', 'nomePais', 'nome_pais']) || 'Pais';
      var leagueName = this.primeiroValorAuto(jogador, ['leagueName', 'league_name', 'nomeLiga', 'nome_liga']) || 'Liga';
      var price = this.primeiroValorAuto(jogador, ['price', 'futggPrice']);
      return {
        id: String(definitionId),
        text: nome + (posicao ? ' (' + posicao + ')' : '') + ' #' + definitionId,
        name: nome,
        definitionId: String(definitionId),
        resourceId: String(definitionId),
        assetId: String(assetId || ''),
        rating: rating,
        cardType: cardType,
        teamId: teamId,
        nationId: nationId,
        leagueId: leagueId,
        teamName: teamName,
        nationName: nationName,
        leagueName: leagueName,
        preferredPosition: posicao,
        rareflag: rareflag,
        ratingTier: ratingTier,
        price: price,
        playerImgUrl: this.montarUrlFutnextAuto('player', assetId || definitionId),
        nationImgUrl: this.montarUrlFutnextAuto('nation', nationId),
        clubImgUrl: this.montarUrlFutnextAuto('club', teamId),
        leagueImgUrl: this.montarUrlFutnextAuto('league', leagueId),
        rarityImgUrl: this.montarUrlFutnextAuto('rarity', rareflag),
      };
    }

    private inicializarSelectJogadorAuto() {
      if (!this.elAutoCarta.length || this.elAutoCarta.data('select2')) return;
      (this.elAutoCarta as any).select2({
        dropdownParent: this.modal,
        placeholder: 'Digite ao menos 3 letras do nome',
        allowClear: true,
        // 3 caracteres + 500ms: cada tecla passou a custar uma requisicao ao servidor.
        minimumInputLength: 3,
        width: '100%',
        ajax: {
          delay: 500,
          transport: (params: any, success: any, failure: any) => {
            var termo = params && params.data ? (params.data.term || params.data.q || '') : '';
            var requisicao: any = null;
            if (String(termo).trim().length < 3) {
              success({ results: [] });
            } else {
              requisicao = this.buscarConceitosAuto(String(termo).trim());
              requisicao.then((resultados: any[]) => {
                success({ results: resultados });
              }, (erro: any) => failure(erro));
            }
            return {
              abort: () => {
                if (requisicao && typeof requisicao.abort === 'function') {
                  try { requisicao.abort(); } catch (e) { /* jqXHR ja finalizado */ }
                }
              },
            };
          },
        },
        templateResult: (item: any) => this.montarCardJogadorAuto(item),
        templateSelection: (item: any) => this.montarCardJogadorAuto(item, true),
      });
      this.elAutoCarta.on('select2:select select2:clear change', () => {
        this.autoOperationId = null;
        this.autoCalculoAtual = null;
        this.renderizarMetricasAuto(null);
        this.atualizarBotaoConfirmarAuto();
      });
    }

    private montarCardJogadorAuto(item: any, selecionado: boolean = false): any {
      if (!item || item.loading || !item.id) return item ? item.text : '';
      var container = $('<div/>')
        .addClass('teste-preco-player-card')
        .toggleClass('teste-preco-player-card-selected', selecionado);
      var thumb = $('<div/>')
        .addClass('teste-preco-player-thumb')
        .css('background-image', item.rarityImgUrl ? 'url(' + item.rarityImgUrl + ')' : '')
        .appendTo(container);
      var addMetaIcon = function (url: string, titleText: string, meta: JQuery) {
        if (!url) return;
        $('<img/>')
          .addClass('teste-preco-player-meta-icon')
          .attr('src', url)
          .attr('alt', titleText)
          .attr('title', titleText)
          .on('error', function () { $(this).remove(); })
          .appendTo(meta);
      };
      var fallback = $('<span/>')
        .addClass('teste-preco-player-fallback')
        .text(String(item.name || '?').substring(0, 1).toUpperCase())
        .appendTo(thumb);
      if (item.playerImgUrl) {
        fallback.hide();
        $('<img/>')
          .addClass('teste-preco-player-photo')
          .attr('src', item.playerImgUrl)
          .attr('alt', item.name || 'Jogador')
          .on('error', function () {
            $(this).remove();
            fallback.show();
          })
          .appendTo(thumb);
      }

      var body = $('<div/>').addClass('teste-preco-player-body').appendTo(container);
      var title = $('<div/>').addClass('teste-preco-player-title').appendTo(body);
      $('<span/>').addClass('teste-preco-player-name').text(item.name || item.text || 'Jogador').appendTo(title);
      if (item.preferredPosition) {
        $('<span/>').addClass('teste-preco-player-position').text(item.preferredPosition).appendTo(title);
      }
      var meta = $('<div/>')
        .addClass('teste-preco-player-meta')
        .append($('<span/>').addClass('teste-preco-player-id').text('#' + (item.definitionId || item.resourceId || item.id || '-')))
        .appendTo(body);
      addMetaIcon(item.clubImgUrl, item.teamName || 'Clube', meta);
      addMetaIcon(item.nationImgUrl, item.nationName || 'Pais', meta);
      addMetaIcon(item.leagueImgUrl, item.leagueName || 'Liga', meta);

      var side = $('<div/>').addClass('teste-preco-player-side').appendTo(container);
      var rarityRow = $('<span/>').addClass('teste-preco-player-rarity-row').appendTo(side);
      $('<span/>').addClass('teste-preco-player-rarity').text(item.cardType || ('Rareflag ' + (item.rareflag || '-'))).appendTo(rarityRow);
      if (item.rarityImgUrl) {
        $('<img/>')
          .addClass('teste-preco-player-rarity-img')
          .attr('src', item.rarityImgUrl)
          .attr('alt', 'Raridade')
          .attr('title', item.cardType || 'Raridade')
          .on('error', function () { $(this).remove(); })
          .appendTo(rarityRow);
      }
      if (Number(item.price || 0) > 0) {
        $('<span/>').addClass('teste-preco-player-price').text(this.formatCoinsAuto(item.price)).appendTo(side);
      }
      $('<span/>')
        .addClass('teste-preco-player-rating')
        .text(item.rating || '-')
        .appendTo(container);
      return container;
    }

    private obterJogadorAutoSelecionado(): any {
      var dados = this.elAutoCarta.data('select2') ? (this.elAutoCarta as any).select2('data') : null;
      var item = dados && dados.length ? dados[0] : null;
      var resourceId = Number(item && (item.resourceId || item.definitionId || item.id) || this.elAutoCarta.val() || 0);
      if (!resourceId || isNaN(resourceId)) return null;
      return $.extend({}, item || {}, { resourceId: resourceId, definitionId: resourceId });
    }

    private limparAutoUi() {
      this.autoOperationId = null;
      this.autoCalculoAtual = null;
      this.autoExecutando = false;
      this.elAutoMargem.val('1');
      this.elAutoCarta.val(null).trigger('change');
      this.elAutoBtnCalcular.prop('disabled', false).html(FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML);
      this.elAutoBtnConfirmar.prop('disabled', true);
      this.elAutoBtnCancelar.prop('disabled', true);
      this.aplicarEtapaAuto(null, 'info');
      this.renderizarMetricasAuto(null);
    }

    private aplicarEtapaAuto(etapa: string, tipo: 'info' | 'sucesso' | 'erro' = 'info') {
      var ordem = ['preparacao', 'preco_ea', 'compra_a', 'transfer_manual', 'transfer_concluida', 'relistagem_b', 'monitor_venda'];
      if (!etapa) {
        this.elAutoSteps.find('.transfer-auto-step').removeClass('is-done is-active is-error');
        return;
      }
      var indice = ordem.indexOf(etapa);
      if (indice < 0) return;
      this.elAutoSteps.find('.transfer-auto-step').removeClass('is-done is-active is-error');
      for (var i = 0; i < indice; i++) {
        this.elAutoSteps.find('[data-step="' + ordem[i] + '"]').addClass('is-done');
      }
      var el = this.elAutoSteps.find('[data-step="' + etapa + '"]');
      if (tipo === 'erro') el.addClass('is-error');
      else el.addClass(tipo === 'sucesso' ? 'is-done' : 'is-active');
    }

    private renderizarMetricasAuto(calculo: any) {
      var c = calculo || {};
      var set = (nome: string, valor: string) => this.elAutoMetricas.find('[data-metrica="' + nome + '"]').text(valor || '—');
      set('precoEA', this.formatCoinsAuto(c.precoEA));
      set('limites', c.precoMinimo || c.precoMaximo ? (this.formatCoinsAuto(c.precoMinimo) + ' / ' + this.formatCoinsAuto(c.precoMaximo)) : '—');
      set('precoListagemA', this.formatCoinsAuto(c.precoListagemA));
      set('valorPagoB', this.formatCoinsAuto(c.precoListagemA));
      var textoRevenda = this.formatCoinsAuto(c.revendaB);
      if (c.revendaCorrigida && c.revendaB) textoRevenda += ' (corrigido)';
      set('revendaB', textoRevenda);
      set('taxaEa', this.formatCoinsAuto(c.taxaEa));
      set('liquidoB', this.formatCoinsAuto(c.liquidoB));
      set('coinsEstimadas', this.formatCoinsAuto(c.coinsEstimadas));
      set('statusVenda', c.statusVenda || '—');
    }

    private contaSelecionadaParaTransfer(lado: 'a' | 'b'): boolean {
      if (lado === 'a') return this.modoContaATemp ? !!this.contaATemp : !!this.contaA;
      return this.modoContaBTemp ? !!this.contaBTemp : !!this.contaB;
    }

    private liberarCalculoAutoAposFalha() {
      this.autoExecutando = false;
      this.elAutoBtnCalcular.prop('disabled', false).html(FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML);
      this.elAutoBtnConfirmar.prop('disabled', true);
      this.elAutoBtnCancelar.prop('disabled', true);
    }

    private prepararContaAsync(lado: 'seller' | 'buyer'): Promise<boolean> {
      return new Promise((resolve) => {
        this.prepararConta(lado, (ok: boolean) => resolve(ok === true));
      });
    }

    private async prepararContasParaCalculoAuto(): Promise<boolean> {
      if (this.contasSelecionadasComPlataformaDiferente()) {
        Util.exibirAlertaNotificacaoToastr(
          'Plataformas incompatíveis',
          'Não é possível transferir entre PC e consola (PlayStation ou Xbox). Entre PlayStation e Xbox é permitido.',
          'warning'
        );
        return false;
      }

      var promessas: Promise<boolean>[] = [];
      if (!this.contaAPronta) promessas.push(this.prepararContaAsync('seller'));
      if (!this.contaBPronta) promessas.push(this.prepararContaAsync('buyer'));
      if (promessas.length) {
        var resultados = await Promise.all(promessas);
        if (resultados.indexOf(false) >= 0) return false;
      }

      if (!this.getIdContaAPlugin() || !this.getIdContaBPlugin()) {
        Util.exibirAlertaNotificacaoToastr(
          'Sessão inválida',
          'Não foi possível obter as sessões preparadas da Conta A e da Conta B.',
          'warning'
        );
        return false;
      }

      return true;
    }

    private montarPayloadAuto(confirmado: boolean = false): any {
      var contaAId = this.getIdContaAPlugin();
      var contaBId = this.getIdContaBPlugin();
      var coinsObjetivo = Number(this.elAutoCoins.val());
      var configRevenda = this.obterConfigRevendaAuto(this.obterLimitesRevendaAuto(this.autoCalculoAtual));
      var margemPct = configRevenda.margemPct;
      var jogador = this.obterJogadorAutoSelecionado();
      var resourceId = jogador ? Number(jogador.resourceId || jogador.definitionId || 0) : null;
      return {
        contaAId,
        contaBId,
        coinsObjetivo,
        margemPct,
        modoRevenda: configRevenda.modoRevenda,
        entradaRevenda: configRevenda.entradaRevenda,
        valorRevendaInformado: configRevenda.valorRevendaInformado,
        revendaBUsuario: configRevenda.revendaBUsuario,
        entradaCarta: jogador ? (jogador.name || jogador.text || String(resourceId)) : '',
        nomeJogador: jogador ? (jogador.name || jogador.text || '') : '',
        resourceId,
        definitionId: resourceId,
        confirmado,
        operationId: this.autoOperationId,
        emails: {
          contaA: this.contaA ? (this.contaA.email || null) : (this.contaATemp ? this.contaATemp.email : null),
          contaB: this.contaB ? (this.contaB.email || null) : (this.contaBTemp ? this.contaBTemp.email : null),
        },
        idsCadastro: {
          contaA: this.contaA ? this.contaA.id : null,
          contaB: this.contaB ? this.contaB.id : null,
        }
      };
    }

    private async calcularTransferAutomatica() {
      if (this.autoExecutando) return;
      if (!this.contaSelecionadaParaTransfer('a') || !this.contaSelecionadaParaTransfer('b')) {
        Util.exibirAlertaNotificacaoToastr('Contas inválidas', 'Selecione Conta A e Conta B antes de calcular.', 'warning');
        return;
      }
      var payload = this.montarPayloadAuto(false);
      if (!payload.coinsObjetivo || payload.coinsObjetivo < 1) {
        Util.exibirAlertaNotificacaoToastr('Coins inválidas', 'Informe a quantidade de coins que deseja transferir.', 'warning');
        return;
      }
      if (!payload.resourceId) {
        Util.exibirAlertaNotificacaoToastr('Jogador inválido', 'Selecione o jogador antes de calcular.', 'warning');
        return;
      }

      this.autoExecutando = true;
      this.elAutoBtnCalcular.prop('disabled', true).html('<i class="fas fa-spinner fa-spin mr-1"></i>Preparando e calculando...');
      this.elAutoBtnConfirmar.prop('disabled', true);
      this.elAutoBtnCancelar.prop('disabled', false);
      this.atualizarGeralStatus('Preparando contas...', 'info');
      this.aplicarEtapaAuto('preparacao', 'info');

      try {
        var resourceIdResolvido = payload.resourceId;
        this.adicionarLog('Jogador selecionado. Preparando contas para confirmar preço EA...', 'info');
        var preparadas = await this.prepararContasParaCalculoAuto();
        if (!preparadas) {
          this.liberarCalculoAutoAposFalha();
          return;
        }
        this.adicionarLog('Contas preparadas. Consultando preço real pela Conta A...', 'info');

        payload = this.montarPayloadAuto(false);
        payload.resourceId = resourceIdResolvido;
        payload.definitionId = resourceIdResolvido;
        if (!payload.contaAId || !payload.contaBId) {
          Util.exibirAlertaNotificacaoToastr('Contas inválidas', 'Não foi possível preparar Conta A e Conta B antes de calcular.', 'warning');
          this.liberarCalculoAutoAposFalha();
          return;
        }

        this.atualizarGeralStatus('Consultando preço EA...', 'info');
        this.aplicarEtapaAuto('preco_ea', 'info');
        this.adicionarLog('Solicitando ao plugin a busca da carta no mercado EA e o cálculo real de viabilidade...', 'info');

        this.comunicacao.enviar(Util.ACAO_TRANSFER_AUTOMATICA_EXECUTAR, payload, (response: IRespostaMensagem) => {
          this.autoExecutando = false;
          this.elAutoBtnCalcular.prop('disabled', false).html(FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML);
          this.elAutoBtnCancelar.prop('disabled', true);
          if (!response.status) {
            var resultFalha: any = response.result || {};
            console.log('[FSW_TRANSFER_AUTO][UI][CALCULO_FALHA]', resultFalha);
            if (resultFalha.calculo) {
              this.autoCalculoAtual = resultFalha.calculo;
              this.renderizarMetricasAuto(this.autoCalculoAtual);
              this.aplicarRevendaCorrigidaAoCampoAuto(this.autoCalculoAtual);
              this.aplicarEtapaAuto('preco_ea', 'erro');
            }
            this.elAutoBtnConfirmar.prop('disabled', true);
            var msg = response.result && (response.result as any).mensagem ? (response.result as any).mensagem : 'Falha ao calcular transferência semiautomática.';
            this.adicionarLog(msg, 'erro');
            this.atualizarGeralStatus('Falhou', 'erro');
            return;
          }
          var result: any = response.result || {};
          this.autoOperationId = result.operationId || null;
          this.autoCalculoAtual = result.calculo || null;
          this.renderizarMetricasAuto(this.autoCalculoAtual);
          this.aplicarRevendaCorrigidaAoCampoAuto(this.autoCalculoAtual);
          this.aplicarEtapaAuto('preco_ea', 'sucesso');
          this.atualizarBotaoConfirmarAuto();
          this.atualizarGeralStatus('Aguardando confirmação', 'info');
        });
      } catch (e) {
        var detalhe = e && (e as Error).message ? (e as Error).message : String(e);
        this.adicionarLog('Falha ao continuar cálculo semiautomático após preparar contas: ' + detalhe, 'erro');
        this.atualizarGeralStatus('Falhou', 'erro');
        this.liberarCalculoAutoAposFalha();
        return;
      }
    }

    private confirmarTransferAutomatica() {
      if (!this.autoOperationId || this.autoExecutando) return;
      this.recalcularTransferAutoComRevendaAtual(false);
      if (!this.autoCalculoAtual || this.autoCalculoAtual.viavel !== true) {
        this.atualizarBotaoConfirmarAuto();
        this.adicionarLog(this.autoCalculoAtual && this.autoCalculoAtual.motivo ? this.autoCalculoAtual.motivo : 'Ajuste os dados da transferência semiautomática antes de confirmar.', 'erro');
        return;
      }
      var payload = this.montarPayloadAuto(true);
      payload.calculoConfirmado = this.autoCalculoAtual;
      this.autoExecutando = true;
      this.autoAudioTransferManualTocado = false;
      this.elAutoBtnConfirmar.prop('disabled', true).html('<i class="fas fa-spinner fa-spin mr-1"></i>Transferindo...');
      this.elAutoBtnCalcular.prop('disabled', true);
      this.elAutoBtnCancelar.prop('disabled', false);
      this.atualizarGeralStatus('Transferência semiautomática em andamento...', 'info');

      this.comunicacao.enviar(Util.ACAO_TRANSFER_AUTOMATICA_EXECUTAR, payload, (response: IRespostaMensagem) => {
        this.autoExecutando = false;
        this.elAutoBtnConfirmar.html('<i class="fas fa-check mr-1"></i>Confirmar e transferir');
        this.elAutoBtnCalcular.prop('disabled', false).html(FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML);
        this.elAutoBtnCancelar.prop('disabled', true);
        if (!response.status) {
          var msg = response.result && (response.result as any).mensagem ? (response.result as any).mensagem : 'Falha na transferência semiautomática.';
          this.adicionarLog(msg, 'erro');
          this.atualizarGeralStatus('Falhou', 'erro');
          this.tocarAudioResultadoTransferencia(false);
          return;
        }
        var result: any = response.result || {};
        if (result.status === 'nova_confirmacao') {
          this.autoCalculoAtual = result.calculo || this.autoCalculoAtual;
          this.renderizarMetricasAuto(this.autoCalculoAtual);
          this.aplicarRevendaCorrigidaAoCampoAuto(this.autoCalculoAtual);
          this.atualizarBotaoConfirmarAuto();
          this.atualizarGeralStatus('Nova confirmação necessária', 'info');
          toastr.warning('O cálculo mudou. Confira os novos valores antes de confirmar novamente.');
          return;
        }
        if (result.calculo) this.renderizarMetricasAuto(result.calculo);
        this.elAutoMetricas.find('[data-metrica="statusVenda"]').text(result.status === 'timeout_venda' ? 'Timeout' : 'Confirmada');
        this.atualizarGeralStatus(result.status === 'timeout_venda' ? 'Venda em acompanhamento excedida' : 'Concluído', result.status === 'timeout_venda' ? 'info' : 'sucesso');
        if (!this.autoAudioTransferManualTocado) this.tocarAudioResultadoTransferencia(true);
      });
    }

    private cancelarTransferAutomatica() {
      if (!this.autoOperationId) return;
      this.comunicacao.enviar(Util.ACAO_TRANSFER_AUTOMATICA_CANCELAR, { operationId: this.autoOperationId }, () => {
        this.autoExecutando = false;
        this.elAutoBtnCalcular.prop('disabled', false).html(FSW_AUTO_BTN_PREPARAR_CALCULAR_HTML);
        this.elAutoBtnConfirmar.prop('disabled', true).html('<i class="fas fa-check mr-1"></i>Confirmar e transferir');
        this.elAutoBtnCancelar.prop('disabled', true);
        this.atualizarGeralStatus('SemiAutomático cancelado', 'info');
      });
    }

    // ─── Latência ────────────────────────────────────────────────────────────

    private iniciarAtualizacaoLatencia() {
      this.pararAtualizacaoLatencia();
      this.atualizarLatenciaPlugin();
      this.intervalLatencia = setInterval(() => this.atualizarLatenciaPlugin(), 5000);
    }

    private pararAtualizacaoLatencia() {
      if (this.intervalLatencia) {
        clearInterval(this.intervalLatencia);
        this.intervalLatencia = null;
      }
    }

    private atualizarLatenciaPlugin() {
      this.comunicacao.enviar(Util.ACAO_COMUNICACAO_GET_DELAY, null, (response: IRespostaMensagem) => {
        if (response.status && response.result != null && typeof response.result === 'number') {
          var ms = response.result as number;
          this.elLatency.text(ms.toFixed(2) + ' ms');
          this.elLatency.toggleClass('text-danger', ms > 100).toggleClass('text-success', ms <= 100);
        } else {
          this.elLatency.text('—').removeClass('text-danger text-success');
        }
      });
    }

    // ─── Heartbeat de sessão (5s) ─────────────────────────────────────────────

    private iniciarHeartbeat() {
      this.pararHeartbeat();
      this.intervalHeartbeat = setInterval(() => this.verificarSessoes(), 5000);
    }

    private pararHeartbeat() {
      if (this.intervalHeartbeat) {
        clearInterval(this.intervalHeartbeat);
        this.intervalHeartbeat = null;
      }
    }

    private verificarSessoes() {
      var contaAId = this.getIdContaAPlugin();
      var contaBId = this.getIdContaBPlugin();
      if (!contaAId) return;

      this.comunicacao.enviar(Util.ACAO_TRANSFER_VERIFICAR_SESSAO, { contaAId, contaBId }, (response: IRespostaMensagem) => {
        if (!response.status) return;
        var result = response.result as any;
        var quedaA = result && result.statusA === false;
        var quedaB = result && result.statusB === false;

        if (quedaA || quedaB) {
          this.pararHeartbeat();
          var ladoFechado = quedaA ? 'A (vendedora)' : 'B (compradora)';
          this.adicionarLog(
            'WebApp da conta ' + ladoFechado + ' foi fechado manualmente — sessão encerrada. Reprepare as contas A e B para sincronizar novamente.',
            'erro'
          );
          // O backend encerra o par A+B para evitar dessincronização;
          // a UI deve refletir ambos como inativos imediatamente.
          this.contaAPronta = false;
          this.contaBPronta = false;
          this.atualizarStatusBadge(this.elStatusA, 'INATIVA', null);
          this.atualizarStatusBadge(this.elStatusB, 'INATIVA', null);
          this.elBtnPrepararA.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
          this.elBtnPrepararB.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
          this.comunicacao.enviar(Util.ACAO_TRANSFER_FECHAR_CONTAS, { contaAId, contaBId }, () => {});
          this.atualizarGeralStatus('Sessão encerrada — reprepare A e B', 'erro');
        }
      });
    }

    // ─── Eventos ─────────────────────────────────────────────────────────────

    private inicializarEventos() {
      this.elBtnPrepararA.on('click', () => this.prepararConta('seller'));
      this.elBtnPrepararB.on('click', () => this.prepararConta('buyer'));
      this.elBtnSwap.on('click', () => this.inverterContas());
      this.elBtnExecutar.on('click', () => this.confirmarEExecutar());
      this.elBtnReset.on('click', () => this.reset());
      this.elTabLinks.on('shown.bs.tab click', (e) => this.alternarAbaTransfer($(e.currentTarget).data('transferTab') || 'manual'));
      this.elAutoBtnCalcular.on('click', () => this.calcularTransferAutomatica());
      this.elAutoBtnConfirmar.on('click', () => this.confirmarTransferAutomatica());
      this.elAutoBtnCancelar.on('click', () => this.cancelarTransferAutomatica());
      this.elAutoCoins.on('input change blur', () => this.recalcularTransferAutoComRevendaAtual(false));
      this.elAutoMargem.on('input change', () => this.recalcularTransferAutoComRevendaAtual(false));
      this.elAutoMargem.on('blur', () => this.recalcularTransferAutoComRevendaAtual(true));

      this.elInputPreco.on('input', () => this.atualizarTax());
      this.elInputPerdaVenda.on('input change', () => this.atualizarTax());
      this.elInputPerdaVenda.on('blur', () => {
        var v = parseFloat(this.elInputPerdaVenda.val() as string);
        if (isNaN(v)) {
          this.elInputPerdaVenda.val(String(FSW_PERDA_VENDA_PADRAO));
        } else {
          this.elInputPerdaVenda.val(String(this.clampPerdaVendaPct(v)));
        }
        this.atualizarTax();
      });
      this.elSelectJogador.on('change', () => this.onJogadorSelecionado());

      this.elEmailA.on('click', () => this.toggleDropdownCred('a'));
      this.elEmailB.on('click', () => this.toggleDropdownCred('b'));

      $(document).on('click.transferDropdown', (e) => {
        if (!$(e.target).closest(
          '.transfer-conta-a-email, .transfer-a-dropdown, .transfer-conta-b-email, .transfer-b-dropdown'
        ).length) {
          this.elADropdown.hide();
          this.elBDropdown.hide();
        }
      });

      this.elASearch.on('input', () => {
        if (this.elADropdown.is(':visible')) this.renderizarDropdownCred('a');
      });
      this.elBSearch.on('input', () => {
        if (this.elBDropdown.is(':visible')) this.renderizarDropdownCred('b');
      });

      this.modal.find('.btn-transfer-a-add').on('click', () => this.abrirFormCred('a', null));
      this.modal.find('.btn-transfer-a-edit').on('click', () => {
        if (this.contaATemp) this.abrirFormCred('a', this.contaATemp.id);
        else toastr.warning('Selecione uma conta primeiro.');
      });
      this.modal.find('.btn-transfer-a-del').on('click', () => this.excluirCredSelecionada('a'));
      this.modal.find('.btn-transfer-a-form-save').on('click', () => this.salvarCredForm('a'));
      this.modal.find('.btn-transfer-a-form-cancel').on('click', () => {
        this.elAForm.hide();
        this.editandoContaTempIdA = null;
      });

      this.modal.find('.btn-transfer-b-add').on('click', () => this.abrirFormCred('b', null));
      this.modal.find('.btn-transfer-b-edit').on('click', () => {
        if (this.contaBTemp) this.abrirFormCred('b', this.contaBTemp.id);
        else toastr.warning('Selecione uma conta primeiro.');
      });
      this.modal.find('.btn-transfer-b-del').on('click', () => this.excluirCredSelecionada('b'));
      this.modal.find('.btn-transfer-b-form-save').on('click', () => this.salvarCredForm('b'));
      this.modal.find('.btn-transfer-b-form-cancel').on('click', () => {
        this.elBForm.hide();
        this.editandoContaTempIdB = null;
      });

      this.modal.on('hidden.bs.modal', () => {
        this.pararAtualizacaoLatencia();
        this.pararHeartbeat();
        $(document).off('click.transferDropdown');
        if (!this.executando && !this.autoExecutando) this.reset(true);
      });
    }

    // ─── Contas fora do cadastro (localStorage) ──────────────────────────────

    private carregarContasOff(): IContaTemporaria[] {
      try {
        var raw = localStorage.getItem(LS_CONTAS_OFF);
        if (raw) return JSON.parse(raw);
        var leg = localStorage.getItem(LS_CONTAS_TEMP_LEGACY);
        if (leg) {
          var arr = JSON.parse(leg) as IContaTemporaria[];
          var migrated: IContaTemporaria[] = [];
          for (var i = 0; i < arr.length; i++) {
            var c = arr[i];
            var norm = normalizarEmail(c.email);
            if (!norm) continue;
            migrated.push({
              id: idPorEmailNormalizado(norm),
              email: norm,
              senhaOfuscada: c.senhaOfuscada,
              backup: c.backup,
            });
          }
          this.salvarContasOff(migrated);
          return migrated;
        }
      } catch (e) { /* ignore */ }
      return [];
    }

    private salvarContasOff(contas: IContaTemporaria[]) {
      localStorage.setItem(LS_CONTAS_OFF, JSON.stringify(contas));
    }

    private filtrarContasPorPesquisa(contas: IContaTemporaria[], q: string): IContaTemporaria[] {
      var needle = (q || '').trim().toLowerCase();
      if (!needle) return contas;
      return contas.filter((c) => (c.email || '').toLowerCase().indexOf(needle) !== -1);
    }

    private renderizarDropdownCred(lado: 'a' | 'b') {
      var modo = lado === 'a' ? this.modoContaATemp : this.modoContaBTemp;
      if (!modo) return;
      var q = lado === 'a' ? (this.elASearch.val() as string) : (this.elBSearch.val() as string);
      var contas = this.filtrarContasPorPesquisa(this.carregarContasOff(), q);
      var lista = lado === 'a' ? this.elADropdownLista : this.elBDropdownLista;
      var vazio = lado === 'a' ? this.elADropdownVazio : this.elBDropdownVazio;
      var sel = lado === 'a' ? this.contaATemp : this.contaBTemp;
      lista.empty();
      if (contas.length === 0) {
        vazio.show();
      } else {
        vazio.hide();
        for (var i = 0; i < contas.length; i++) {
          var c = contas[i];
          var item = $('<div class="px-2 py-1 transfer-cred-dropdown-item" style="cursor:pointer;">' + c.email + '</div>');
          item.on('click', ((cc: IContaTemporaria, L: 'a' | 'b') => () => this.selecionarCred(L, cc))(c, lado));
          if (sel && sel.id === c.id) {
            item.addClass('font-weight-bold bg-light');
          }
          lista.append(item);
        }
      }
    }

    private toggleDropdownCred(lado: 'a' | 'b') {
      var modo = lado === 'a' ? this.modoContaATemp : this.modoContaBTemp;
      if (!modo) return;
      var dd = lado === 'a' ? this.elADropdown : this.elBDropdown;
      var outro = lado === 'a' ? this.elBDropdown : this.elADropdown;
      outro.hide();
      if (dd.is(':visible')) {
        dd.hide();
      } else {
        this.renderizarDropdownCred(lado);
        dd.show();
      }
    }

    private selecionarCred(lado: 'a' | 'b', c: IContaTemporaria) {
      if (lado === 'a') {
        this.contaATemp = c;
        this.elEmailA.text(c.email);
        this.elADropdown.hide();
        this.contaAPronta = false;
        this.atualizarStatusBadge(this.elStatusA, 'INATIVA', null);
        this.elBtnPrepararA.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
        this.adicionarLog('Conta vendedora (A) selecionada: ' + c.email, 'info');
      } else {
        this.contaBTemp = c;
        this.elEmailB.text(c.email);
        this.elBDropdown.hide();
        this.contaBPronta = false;
        this.atualizarStatusBadge(this.elStatusB, 'INATIVA', null);
        this.elBtnPrepararB.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
        this.adicionarLog('Conta compradora (B) selecionada: ' + c.email, 'info');
      }
    }

    private abrirFormCred(lado: 'a' | 'b', id: string | null) {
      if (lado === 'a') this.elADropdown.hide();
      else this.elBDropdown.hide();
      if (lado === 'a') {
        this.editandoContaTempIdA = id;
        if (id) {
          var contas = this.carregarContasOff();
          var conta = contas.find((c) => c.id === id);
          if (conta) {
            this.elAFormEmail.val(conta.email);
            this.elAFormSenha.val(decodificarSenhaArmazenada(conta.senhaOfuscada));
            this.elAFormBackup.val(conta.backup || '');
          }
        } else {
          this.elAFormEmail.val('');
          this.elAFormSenha.val('');
          this.elAFormBackup.val('');
        }
        this.elAForm.show();
      } else {
        this.editandoContaTempIdB = id;
        if (id) {
          var contasB = this.carregarContasOff();
          var contaB = contasB.find((c) => c.id === id);
          if (contaB) {
            this.elBFormEmail.val(contaB.email);
            this.elBFormSenha.val(decodificarSenhaArmazenada(contaB.senhaOfuscada));
            this.elBFormBackup.val(contaB.backup || '');
          }
        } else {
          this.elBFormEmail.val('');
          this.elBFormSenha.val('');
          this.elBFormBackup.val('');
        }
        this.elBForm.show();
      }
    }

    private salvarCredForm(lado: 'a' | 'b') {
      var elEmail = lado === 'a' ? this.elAFormEmail : this.elBFormEmail;
      var elSenha = lado === 'a' ? this.elAFormSenha : this.elBFormSenha;
      var elBackup = lado === 'a' ? this.elAFormBackup : this.elBFormBackup;
      var email = (elEmail.val() as string).trim();
      var senha = (elSenha.val() as string);
      var backup = (elBackup.val() as string).trim();
      if (!email || !senha) {
        toastr.warning('E-mail e senha são obrigatórios.');
        return;
      }
      var norm = normalizarEmail(email);
      var idStable = idPorEmailNormalizado(norm);
      var contas = this.carregarContasOff();
      var idx = contas.findIndex((c) => normalizarEmail(c.email) === norm);
      var editId = lado === 'a' ? this.editandoContaTempIdA : this.editandoContaTempIdB;
      var novaEntrada: IContaTemporaria = {
        id: idStable,
        email: norm,
        senhaOfuscada: codificarSenhaParaArmazenar(senha),
        backup: backup || undefined,
      };
      if (idx >= 0) {
        contas[idx] = novaEntrada;
      } else if (editId) {
        contas = contas.map((c) => (c.id === editId ? novaEntrada : c));
      } else {
        contas.push(novaEntrada);
      }
      this.salvarContasOff(contas);
      if (lado === 'a') {
        this.elAForm.hide();
        this.editandoContaTempIdA = null;
        if (this.contaATemp && (this.contaATemp.id === editId || normalizarEmail(this.contaATemp.email) === norm)) {
          this.contaATemp = novaEntrada;
          this.elEmailA.text(norm);
        }
      } else {
        this.elBForm.hide();
        this.editandoContaTempIdB = null;
        if (this.contaBTemp && (this.contaBTemp.id === editId || normalizarEmail(this.contaBTemp.email) === norm)) {
          this.contaBTemp = novaEntrada;
          this.elEmailB.text(norm);
        }
      }
      toastr.success('Conta salva.');
    }

    private excluirCredSelecionada(lado: 'a' | 'b') {
      var temp = lado === 'a' ? this.contaATemp : this.contaBTemp;
      if (!temp) {
        toastr.warning('Selecione uma conta para excluir.');
        return;
      }
      var emailRm = temp.email;
      swal.fire({
        title: 'Excluir credenciais e perfil Chrome?',
        text: 'As credenciais salvas e o perfil Chrome temporário deste e-mail serão removidos no plugin. Na próxima preparação, a EA poderá pedir novo código.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
      }).then((result: any) => {
        if (!result.isConfirmed) return;
        var contas = this.carregarContasOff().filter((c) => c.id !== temp.id);
        this.salvarContasOff(contas);
        this.enviarRemoverPerfilTemp(emailRm);
        if (lado === 'a') {
          this.contaATemp = null;
          this.elEmailA.text('— selecione na lista —');
        } else {
          this.contaBTemp = null;
          this.elEmailB.text('— selecione na lista —');
        }
        toastr.success('Conta excluída.');
      });
    }

    // ─── Preparar contas ─────────────────────────────────────────────────────

    private prepararConta(lado: 'seller' | 'buyer', onFinalizar?: (ok: boolean) => void) {
      var elBtn = lado === 'seller' ? this.elBtnPrepararA : this.elBtnPrepararB;
      var elStatus = lado === 'seller' ? this.elStatusA : this.elStatusB;
      var finalizar = (ok: boolean) => {
        if (typeof onFinalizar === 'function') onFinalizar(ok === true);
      };

      if (this.contasSelecionadasComPlataformaDiferente()) {
        toastr.warning(
          'Não é possível transferir entre PC e consola (PlayStation ou Xbox). Entre PlayStation e Xbox é permitido.'
        );
        finalizar(false);
        return;
      }

      if (lado === 'seller') {
        if (this.modoContaATemp) {
          if (!this.contaATemp) {
            toastr.warning('Selecione uma conta temporária na lista (vendedora).');
            finalizar(false);
            return;
          }
        } else {
          if (!this.contaA) {
            toastr.warning('Selecione a conta vendedora (A) antes.');
            finalizar(false);
            return;
          }
        }
      } else {
        if (this.modoContaBTemp) {
          if (!this.contaBTemp) {
            toastr.warning('Selecione uma conta temporária na lista (compradora).');
            finalizar(false);
            return;
          }
        } else {
          if (!this.contaB) {
            toastr.warning('Selecione a conta compradora (B) antes.');
            finalizar(false);
            return;
          }
        }
      }

      if (lado === 'seller' && this.contaAPronta) {
        this.contaAPronta = false;
        this.pararHeartbeat();
        this.atualizarGeralStatus('A repreparar…', 'info');
      }
      if (lado === 'buyer' && this.contaBPronta) {
        this.contaBPronta = false;
        this.pararHeartbeat();
        this.atualizarGeralStatus('B repreparar…', 'info');
      }

      elBtn.prop('disabled', true).html('<i class="fas fa-spinner fa-spin mr-1"></i> Preparando...');
      var emailLog = lado === 'seller'
        ? (this.modoContaATemp ? this.contaATemp.email : this.contaA.email)
        : (this.modoContaBTemp ? this.contaBTemp.email : this.contaB.email);
      var rotuloLado = lado === 'seller' ? 'vendedora (A)' : 'compradora (B)';
      this.adicionarLog('Preparando conta ' + rotuloLado + ' — ' + emailLog + '...');

      if (lado === 'seller' && this.modoContaATemp) {
        var senhaA = decodificarSenhaArmazenada(this.contaATemp.senhaOfuscada);
        this.comunicacao.enviar(
          Util.ACAO_TRANSFER_PREPARAR_CONTA_A_TEMP,
          { email: this.contaATemp.email, senha: senhaA, backup: this.contaATemp.backup || null },
          (response: IRespostaMensagem) => {
            elBtn.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
            if (response.status) {
              this.adicionarLog('Conta vendedora (A) pronta!', 'sucesso');
              this.atualizarStatusBadge(elStatus, 'PRONTA', 'sucesso');
              this.contaAPronta = true;
              if (response.result && (response.result as any).serverId) {
                (this.contaATemp as any).serverId = (response.result as any).serverId;
              }
              this.carregarJogadores();
              this.tentarIniciarHeartbeat();
              finalizar(true);
            } else {
              var msg = (response.result && (response.result as any).mensagem)
                ? (response.result as any).mensagem
                : 'Falha ao preparar conta A temporária.';
              this.adicionarLog(msg, 'erro');
              this.atualizarStatusBadge(elStatus, 'FALHOU', 'erro');
              this.aposFalhaPreparar('seller');
              finalizar(false);
            }
          }
        );
        return;
      }

      if (lado === 'buyer' && this.modoContaBTemp) {
        var senhaB = decodificarSenhaArmazenada(this.contaBTemp.senhaOfuscada);
        var idA = this.getIdContaAPlugin();
        var request: any = {
          email: this.contaBTemp.email,
          senha: senhaB,
          backup: this.contaBTemp.backup || null,
        };
        if (idA != null) {
          request.contaAId = idA;
        }
        this.comunicacao.enviar(Util.ACAO_TRANSFER_PREPARAR_CONTA_B_TEMP, request, (response: IRespostaMensagem) => {
          elBtn.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
          if (response.status) {
            this.adicionarLog('Conta compradora (B) pronta!', 'sucesso');
            this.atualizarStatusBadge(elStatus, 'PRONTA', 'sucesso');
            this.contaBPronta = true;
            if (response.result && (response.result as any).serverId) {
              (this.contaBTemp as any).serverId = (response.result as any).serverId;
            }
            this.tentarIniciarHeartbeat();
            finalizar(true);
          } else {
            var msg2 = (response.result && (response.result as any).mensagem)
              ? (response.result as any).mensagem
              : 'Falha ao preparar conta B temporária.';
            this.adicionarLog(msg2, 'erro');
            this.atualizarStatusBadge(elStatus, 'FALHOU', 'erro');
            this.aposFalhaPreparar('buyer');
            finalizar(false);
          }
        });
        return;
      }

      var conta = lado === 'seller' ? this.contaA : this.contaB;
      var request2: any = { contaId: conta.id, lado: lado };
      if (lado === 'buyer' && this.getIdContaAPlugin() != null) {
        request2.contaAId = this.getIdContaAPlugin();
      }

      this.comunicacao.enviar(Util.ACAO_TRANSFER_PREPARAR_CONTA, request2, (response: IRespostaMensagem) => {
        elBtn.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
        if (response.status) {
          this.adicionarLog('Conta ' + (lado === 'seller' ? 'vendedora (A)' : 'compradora (B)') + ' pronta!', 'sucesso');
          this.atualizarStatusBadge(elStatus, 'PRONTA', 'sucesso');
          if (lado === 'seller') {
            this.contaAPronta = true;
            this.carregarJogadores();
          } else {
            this.contaBPronta = true;
          }
          this.tentarIniciarHeartbeat();
          finalizar(true);
        } else {
          var msg3 = (response.result && (response.result as any).mensagem)
            ? (response.result as any).mensagem
            : 'Falha ao preparar conta.';
          this.adicionarLog(msg3, 'erro');
          this.atualizarStatusBadge(elStatus, 'FALHOU', 'erro');
          this.aposFalhaPreparar(lado);
          finalizar(false);
        }
      });
    }

    /** Após falha em «Preparar conta»: limpa flag do lado, para heartbeat e alinha o badge geral (evita «Pronto» obsoleto). */
    private aposFalhaPreparar(lado: 'seller' | 'buyer') {
      if (lado === 'seller') this.contaAPronta = false;
      else this.contaBPronta = false;
      this.pararHeartbeat();
      if (this.contaAPronta && this.contaBPronta) {
        this.tentarIniciarHeartbeat();
      } else {
        this.atualizarGeralStatus('Falha na preparação', 'erro');
      }
    }

    private tentarIniciarHeartbeat() {
      if (this.contaAPronta && this.contaBPronta) {
        this.iniciarHeartbeat();
        this.atualizarGeralStatus('Pronto', 'sucesso');
      }
    }

    // ─── Carregar jogadores ───────────────────────────────────────────────────

    private carregarJogadores() {
      var idA = this.getIdContaAPlugin();
      if (!idA) return;
      this.elSelectJogador.html('<option value="">Carregando preços...</option>').prop('disabled', true);
      this.adicionarLog('Buscando lista na pilha de transferências e preços de mercado...');
      this.comunicacao.enviar(Util.ACAO_TRANSFER_GET_JOGADORES, { contaId: idA }, (response: IRespostaMensagem) => {
        if (response.status && response.result && (response.result as any).itens) {
          this.popularSelectJogadores((response.result as any).itens);
        } else {
          this.elSelectJogador.html('<option value="">Falha ao carregar</option>').prop('disabled', false);
          this.adicionarLog('Não foi possível obter a lista de transferência.', 'erro');
        }
      });
    }

    private popularSelectJogadores(itens: any[]) {
      this.elSelectJogador.empty().prop('disabled', false);
      if (!itens || itens.length === 0) {
        this.elSelectJogador.html('<option value="">Nenhum item disponível na lista</option>');
        this.adicionarLog('Nenhum item listável na pilha de transferências.', 'info');
        this.precoMercadoSelecionado = 0;
        this.atualizarTax();
        return;
      }
      this.elSelectJogador.append('<option value="">— selecione o jogador —</option>');
      for (var i = 0; i < itens.length; i++) {
        var item = itens[i];
        var ratingStr = (item.rating !== undefined && item.rating !== null) ? item.rating : '?';
        var pm = (item.precoMercado && item.precoMercado > 0) ? item.precoMercado : 0;
        var refStr = pm > 0 ? (' | ref. ' + pm.toLocaleString('pt-BR')) : '';
        var label = item.name
          ? (item.name + ' (' + ratingStr + ')' + refStr)
          : ('Item ID ' + item.id);
        var $opt = $('<option></option>').attr('value', String(item.id)).text(label);
        $opt.data('item', item);
        this.elSelectJogador.append($opt);
      }
      this.adicionarLog(itens.length + ' item(ns) com preço de referência quando disponível.', 'sucesso');
      this.onJogadorSelecionado();
    }

    private onJogadorSelecionado() {
      var opt = this.elSelectJogador.find('option:selected');
      var item = opt.data('item') as any;
      this.precoMercadoSelecionado = (item && item.precoMercado) ? Number(item.precoMercado) : 0;
      this.atualizarTax();
    }

    // ─── Executar transferência com confirmação ───────────────────────────────

    private confirmarEExecutar() {
      if (!this.contaAPronta || !this.contaBPronta) {
        Util.exibirAlertaNotificacaoToastr('Contas não preparadas', 'Prepare as duas contas antes de transferir.', 'warning');
        return;
      }
      if (this.contasSelecionadasComPlataformaDiferente()) {
        Util.exibirAlertaNotificacaoToastr(
          'Plataformas incompatíveis',
          'Não é possível transferir entre PC e consola (PlayStation ou Xbox). Entre PlayStation e Xbox é permitido.',
          'warning'
        );
        return;
      }
      var selectedOption = this.elSelectJogador.find('option:selected');
      var itemJson = selectedOption.data('item');
      var preco = parseInt(this.elInputPreco.val() as string, 10);
      if (!itemJson || !itemJson.id) {
        Util.exibirAlertaNotificacaoToastr('Jogador não selecionado', 'Selecione um jogador da lista.', 'warning');
        return;
      }
      if (!preco || preco < 150) {
        Util.exibirAlertaNotificacaoToastr('Preço inválido', 'Informe um preço de compra já (BIN) válido na conta A — mínimo 150.', 'warning');
        return;
      }

      var perdaIn = parseFloat(this.elInputPerdaVenda.val() as string);
      var brVal = this.obterBreakdownCalculoTaxa(this.precoMercadoSelecionado, preco, perdaIn);
      if (this.precoMercadoSelecionado > 0 && brVal.coins_registo < 1) {
        Util.exibirAlertaNotificacaoToastr(
          'Valor líquido inválido',
          'Com o preço de referência e o BIN actuais, o valor líquido transferido calculado é inferior a 1 coin. Ajuste o BIN ou o jogador com referência de mercado.',
          'warning'
        );
        return;
      }

      var statusATexto = this.elStatusA.text();
      var statusBTexto = this.elStatusB.text();
      var emailALabel = this.modoContaATemp && this.contaATemp ? this.contaATemp.email : (this.contaA ? this.contaA.email : '—');
      var emailBLabel = this.modoContaBTemp && this.contaBTemp ? this.contaBTemp.email : (this.contaB ? this.contaB.email : '—');

      var msg = '<div class="text-left">'
        + '<p class="mb-2"><strong>Antes de confirmar, verifique:</strong></p>'
        + '<ul class="mb-2 pl-3">'
        + '<li>Preço de venda (BIN conta A): <strong>' + preco.toLocaleString('pt-BR') + ' coins</strong></li>'
        + (this.precoMercadoSelecionado > 0
          ? '<li>Coins líquidas transferidas (estim.): <strong>' + brVal.coins_registo.toLocaleString('pt-BR') + '</strong></li>'
          : '')
        + '<li>Conta A (vendedora): <strong>' + emailALabel + '</strong> — ' + statusATexto + '</li>'
        + '<li>Conta B (compradora): <strong>' + emailBLabel + '</strong> — ' + statusBTexto + '</li>'
        + '<li>PC não transfere para consola nem o contrário; PlayStation e Xbox entre si é permitido.</li>'
        + '<li>A conta B (compradora) tem saldo suficiente?</li>'
        + '<li>A conta B (compradora) consegue comprar jogador(Sem Softban), já testou?</li>'
        + '<li>Os dois WebApps estão abertos e visualmente logados normais?</li>'
        + '</ul>'
        + '</div>';

      swal.fire({
        title: 'Confirmar transferência?',
        html: msg,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, transferir!',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f0ad4e',
      }).then((result: any) => {
        if (!result.isConfirmed) return;
        var attemptId = gerarClientAttemptId();
        this.comunicacao.enviar(Util.ACAO_TRANSFER_FSW_CHECK_BALANCE, { coins: brVal.coins_registo }, (response: IRespostaMensagem) => {
          if (!response.status) {
            var msgCb = (response.result && (response.result as any).mensagem)
              ? (response.result as any).mensagem
              : 'Não foi possível verificar saldo (plugin).';
            toastr.warning(msgCb);
            return;
          }
          var d = response.result as any;
          if (d && d.pode_executar === false) {
            var est = d.custo_estimado != null ? String(d.custo_estimado) : '?';
            toastr.error('Saldo EUR insuficiente para esta operação (custo estimado ' + est + ').');
            return;
          }
          this.executarTransfer(itemJson, preco, attemptId);
        });
      });
    }

    private executarTransfer(itemJson: any, preco: number, clientAttemptId: string) {
      var contaAId = this.getIdContaAPlugin();
      var contaBId = this.getIdContaBPlugin();

      this.executando = true;
      this.elBtnExecutar.prop('disabled', true).html('<i class="fas fa-spinner fa-spin mr-1"></i> Transferindo...');
      this.atualizarGeralStatus('Em andamento...', 'info');
      this.adicionarLog('Iniciando transferência...', 'info');

      var perdaReg = parseFloat(this.elInputPerdaVenda.val() as string);
      var br = this.obterBreakdownCalculoTaxa(this.precoMercadoSelecionado, preco, perdaReg);
      var pmRef = this.precoMercadoSelecionado > 0 ? Math.floor(this.precoMercadoSelecionado) : null;
      var taxaFsw = this.getTaxaFswEurPor100k();
      var custoFswEur = this.custoFswTransferEur(br.coins_registo);
      var metaRegisto: any = {
        calculo_versao: '2026-04-30',
        calculo_taxa: {
          preco_mercado_ref: pmRef,
          perda_na_venda_pct: br.perda_na_venda_pct,
          bin_sugerido: br.bin_sugerido > 0 ? br.bin_sugerido : null,
          base_valor_transferido: br.base_valor_transferido,
          bin_venda_conta_a: preco,
          taxa_ea_venda_decimal: FSW_TAXA_EA_VENDA,
          taxa_ea_venda_sobre_bin: br.taxa_ea_venda_sobre_bin,
          taxa_ref_mercado_decimal: FSW_TAXA_REF_MERCADO,
          taxa_ref_mercado_5pct_sobre_bin_sugerido: br.taxa_ref_mercado_5pct,
          liquido_conta_a_apos_venda: br.liquido_conta_a_apos_venda,
          valor_transferido_liquido: br.valor_transferido_liquido,
          coins_registo: br.coins_registo,
          sem_preco_mercado_ref: br.sem_preco_mercado_ref,
          taxa_fsw_eur_por_100k_coins: taxaFsw,
          custo_fsw_eur_estimado: custoFswEur,
        },
        item: {
          id: itemJson.id,
          name: itemJson.name != null ? String(itemJson.name) : null,
          resourceId: itemJson.resourceId != null ? Number(itemJson.resourceId) : null,
          rating: itemJson.rating != null ? itemJson.rating : null,
          itemType: itemJson.itemType != null ? String(itemJson.itemType) : null,
        },
      };

      this.comunicacao.enviar(Util.ACAO_TRANSFER_EXECUTAR, {
        contaAId: contaAId,
        contaBId: contaBId,
        item: itemJson,
        buyNowPrice: preco,
        startingBid: preco,
        duration: 3600,
        fswRegisterBase: {
          client_attempt_id: clientAttemptId,
          resource_id: itemJson && itemJson.resourceId != null ? Number(itemJson.resourceId) : null,
          coins: br.coins_registo,
          preco_referencia: pmRef,
          id_conta_vendedor: this.contaA ? this.contaA.id : null,
          id_conta_comprador: this.contaB ? this.contaB.id : null,
          email_vendedor: this.contaA ? (this.contaA.email || null) : (this.contaATemp ? this.contaATemp.email : null),
          email_comprador: this.contaB ? (this.contaB.email || null) : (this.contaBTemp ? this.contaBTemp.email : null),
          origem: 'fsw',
          meta: metaRegisto,
        },
      }, (response: IRespostaMensagem) => {
        this.executando = false;
        this.elBtnExecutar.prop('disabled', false).html('<i class="fas fa-exchange-alt mr-1"></i>Transferir');
        var ok = !!response.status;
        var msgErro = ok ? null : ((response.result && (response.result as any).mensagem)
          ? (response.result as any).mensagem
          : 'Falha na transferência.');
        if (ok) {
          this.atualizarGeralStatus('Concluído', 'sucesso');
        } else {
          this.adicionarLog(msgErro, 'erro');
          this.atualizarGeralStatus('Falhou', 'erro');
        }
        this.tocarAudioResultadoTransferencia(ok);
      });
    }

    // ─── Inverter contas ─────────────────────────────────────────────────────

    private inverterContas() {
      if (this.modoContaATemp || this.modoContaBTemp) {
        toastr.warning('Não é possível inverter quando uma das contas usa credenciais fora do cadastro.');
        return;
      }
      var tempConta = this.contaA;
      var tempPronta = this.contaAPronta;
      this.contaA = this.contaB;
      this.contaAPronta = this.contaBPronta;
      this.contaB = tempConta;
      this.contaBPronta = tempPronta;
      this.renderizarContas();
      this.adicionarLog('Contas invertidas: A (vendedora) ↔ B (compradora).', 'info');
      if (this.getIdContaAPlugin() && this.contaAPronta) this.carregarJogadores();
    }

    // ─── Renderização ────────────────────────────────────────────────────────

    private renderizarContas() {
      if (this.modoContaATemp) {
        this.elEmailA.text(this.contaATemp ? this.contaATemp.email : '— selecione na lista —');
      } else {
        this.elEmailA.text(this.contaA ? (this.contaA.email || ('ID ' + this.contaA.id)) : '— selecione na tabela —');
      }
      if (this.modoContaBTemp) {
        this.elEmailB.text(this.contaBTemp ? this.contaBTemp.email : '— selecione na lista —');
      } else {
        this.elEmailB.text(this.contaB ? (this.contaB.email || ('ID ' + this.contaB.id)) : '— selecione na tabela —');
      }
      this.atualizarStatusBadge(this.elStatusA, this.contaAPronta ? 'PRONTA' : 'INATIVA', this.contaAPronta ? 'sucesso' : null);
      this.atualizarStatusBadge(this.elStatusB, this.contaBPronta ? 'PRONTA' : 'INATIVA', this.contaBPronta ? 'sucesso' : null);
      this.atualizarSaldoCoinsContaA();
    }

    private atualizarSaldoCoinsContaA() {
      if (this.modoContaATemp) {
        this.elSessionInfo.text('—');
        return;
      }
      if (!this.contaA || !this.contaA.dadosEa || this.contaA.dadosEa.coins == null || isNaN(Number(this.contaA.dadosEa.coins))) {
        this.elSessionInfo.text('—');
        return;
      }
      var n = Number(this.contaA.dadosEa.coins);
      this.elSessionInfo.text(n.toLocaleString('pt-BR') + ' coins');
    }

    private atualizarStatusBadge(el: JQuery, texto: string, tipo: string | null) {
      el.removeClass('badge-success badge-danger badge-secondary badge-info badge-warning');
      if (tipo === 'sucesso') el.addClass('badge-success');
      else if (tipo === 'erro') el.addClass('badge-danger');
      else el.addClass('badge-secondary');
      el.text(texto);
    }

    private atualizarGeralStatus(texto: string, tipo: string) {
      this.elGeralStatus.removeClass('badge-success badge-danger badge-secondary badge-info badge-warning');
      if (tipo === 'sucesso') this.elGeralStatus.addClass('badge-success');
      else if (tipo === 'erro') this.elGeralStatus.addClass('badge-danger');
      else if (tipo === 'info') this.elGeralStatus.addClass('badge-info');
      else this.elGeralStatus.addClass('badge-secondary');
      this.elGeralStatus.text(this.normalizarTextoTransfer(texto));
    }

    private normalizarTextoTransfer(texto: any): string {
      var normalizado = String(texto == null ? '' : texto);
      var mapa: { [key: string]: string } = {
        '\u00c3\u0080': '\u00c0',
        '\u00c3\u0081': '\u00c1',
        '\u00c3\u0082': '\u00c2',
        '\u00c3\u0083': '\u00c3',
        '\u00c3\u0087': '\u00c7',
        '\u00c3\u0089': '\u00c9',
        '\u00c3\u008a': '\u00ca',
        '\u00c3\u008d': '\u00cd',
        '\u00c3\u0093': '\u00d3',
        '\u00c3\u0094': '\u00d4',
        '\u00c3\u009a': '\u00da',
        '\u00c3\u00a0': '\u00e0',
        '\u00c3\u00a1': '\u00e1',
        '\u00c3\u00a2': '\u00e2',
        '\u00c3\u00a3': '\u00e3',
        '\u00c3\u00a7': '\u00e7',
        '\u00c3\u00a9': '\u00e9',
        '\u00c3\u00aa': '\u00ea',
        '\u00c3\u00ad': '\u00ed',
        '\u00c3\u00b3': '\u00f3',
        '\u00c3\u00b4': '\u00f4',
        '\u00c3\u00ba': '\u00fa',
        '\u00c3\u0097': '\u00d7',
        '\u00e2\u0082\u00ac': '\u20ac',
        '\u00e2\u201a\u00ac': '\u20ac',
        '\u00e2\u0086\u0092': '\u2192',
        '\u00e2\u2020\u2019': '\u2192',
        '\u00e2\u2020\u201d': '\u2194',
        '\u00e2\u0080\u0094': '\u2014',
        '\u00e2\u20ac\u201d': '\u2014',
        '\u00e2\u0080\u0093': '\u2013',
        '\u00e2\u20ac\u201c': '\u2013',
        '\u00e2\u0080\u0098': '\u2018',
        '\u00e2\u20ac\u02dc': '\u2018',
        '\u00e2\u0080\u0099': '\u2019',
        '\u00e2\u20ac\u2122': '\u2019',
        '\u00e2\u0080\u009c': '\u201c',
        '\u00e2\u20ac\u0153': '\u201c',
        '\u00e2\u0080\u009d': '\u201d',
        '\u00e2\u0080\u00a6': '\u2026',
        '\u00e2\u20ac\u00a6': '\u2026',
        '\u00c2': '',
      };
      Object.keys(mapa).forEach(function (chave) {
        normalizado = normalizado.split(chave).join(mapa[chave]);
      });
      return normalizado;
    }

    private escaparHtmlTransfer(texto: string): string {
      return String(texto == null ? '' : texto)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    /** Feedback sonoro ao terminar a ação «Transferir» (callback único; ignora bloqueio de autoplay). */
    private tocarAudioResultadoTransferencia(sucesso: boolean) {
      var src = sucesso ? FSW_AUDIO_TRANSFER_SUCESSO : FSW_AUDIO_TRANSFER_FALHA;
      try {
        var a = new Audio(src);
        a.volume = sucesso ? 0.9 : 0.75;
        var p = a.play();
        if (p && typeof (p as any).catch === 'function') {
          (p as Promise<void>).catch(function () { /* autoplay ou ficheiro ausente */ });
        }
      } catch (e) { /* ignore */ }
    }

    /** Taxa configurada no servidor (€ por 100k coins), injetada no modal via `data-fsw-transfer-taxa-eur-100k`. */
    private getTaxaFswEurPor100k(): number {
      var raw = this.modal.attr('data-fsw-transfer-taxa-eur-100k');
      var n = raw != null && raw !== '' ? parseFloat(String(raw)) : 0;
      return !isNaN(n) && n >= 0 ? n : 0;
    }

    /** Custo FSW em EUR para o registo (mesma base que `UserTransferService`: coins/100k × taxa). */
    private custoFswTransferEur(coinsParaCobranca: number): number {
      var taxa = this.getTaxaFswEurPor100k();
      var c = Math.floor(Number(coinsParaCobranca)) || 0;
      if (taxa <= 0 || c <= 0) return 0;
      return Math.round((c / 100000) * taxa * 100) / 100;
    }

    private formatarEurPt(val: number): string {
      return val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    }

    private clampPerdaVendaPct(raw: number): number {
      if (raw == null || isNaN(raw)) return FSW_PERDA_VENDA_PADRAO;
      var n = Number(raw);
      if (n < 0) return 0;
      if (n > FSW_PERDA_VENDA_MAX) return FSW_PERDA_VENDA_MAX;
      return n;
    }

    /** BIN sugerido: ref. menos % perda, arredondado ao step (ex.: 50). */
    private binSugeridoPorRef(pm: number, perdaPct: number): number {
      if (!pm || pm <= 0 || isNaN(pm)) return 0;
      var p = this.clampPerdaVendaPct(perdaPct);
      var raw = pm * (1 - p / 100);
      var stepped = Math.floor(raw / FSW_BIN_STEP) * FSW_BIN_STEP;
      return Math.max(FSW_BIN_MIN, stepped);
    }

    /**
     * Mesma lógica da calculadora do modal — usada na UI e no registo `user_transfers`.
     * Valor transferido líquido: com ref. usa o **BIN sugerido** (venda provável) ×95% − BIN listado (conta A).
     * `coins_registo`: coluna coins; com ref. = max(0, valor líquido); sem ref. = BIN (legado).
     */
    private obterBreakdownCalculoTaxa(pm: number, bin: number, perdaRaw: number): {
      perda_na_venda_pct: number;
      bin_sugerido: number;
      taxa_ea_venda_sobre_bin: number;
      taxa_ref_mercado_5pct: number;
      liquido_conta_a_apos_venda: number;
      valor_transferido_liquido: number;
      coins_registo: number;
      sem_preco_mercado_ref: boolean;
      base_valor_transferido: 'bin_sugerido' | null;
    } {
      var perdaPct = isNaN(perdaRaw) ? FSW_PERDA_VENDA_PADRAO : this.clampPerdaVendaPct(perdaRaw);
      var binInt = Math.floor(Number(bin)) || 0;
      var pmInt = pm > 0 && !isNaN(Number(pm)) ? Math.floor(Number(pm)) : 0;
      var binSug = pmInt > 0 ? this.binSugeridoPorRef(pmInt, perdaPct) : 0;
      var taxVenda = binInt > 0 ? Math.floor(binInt * FSW_TAXA_EA_VENDA) : 0;
      var liquidoA = binInt > 0 ? Math.floor(binInt - taxVenda) : 0;
      var taxRef = binSug > 0 ? Math.floor(binSug * FSW_TAXA_REF_MERCADO) : 0;
      var valorLiq =
        pmInt > 0 && binInt > 0 && binSug > 0
          ? Math.floor(binSug * (1 - FSW_TAXA_REF_MERCADO) - binInt)
          : 0;
      var semRef = !(pmInt > 0);
      var coinsRegisto = semRef ? binInt : Math.max(0, valorLiq);
      return {
        perda_na_venda_pct: perdaPct,
        bin_sugerido: binSug,
        taxa_ea_venda_sobre_bin: taxVenda,
        taxa_ref_mercado_5pct: taxRef,
        liquido_conta_a_apos_venda: liquidoA,
        valor_transferido_liquido: valorLiq,
        coins_registo: coinsRegisto,
        sem_preco_mercado_ref: semRef,
        base_valor_transferido: pmInt > 0 ? 'bin_sugerido' : null,
      };
    }

    /** Destaque visual do campo “Valor transferido líquido” (verde / vermelho / neutro). */
    private aplicarEstiloValorTransferido(estado: 'vazio' | 'positivo' | 'negativo' | 'zero') {
      var el = this.elValorTransferido;
      el.removeClass(
        'transfer-valor-transferido--positivo transfer-valor-transferido--negativo transfer-valor-transferido--zero bg-light'
      );
      if (estado === 'vazio') {
        el.addClass('bg-light');
        return;
      }
      if (estado === 'positivo') el.addClass('transfer-valor-transferido--positivo');
      else if (estado === 'negativo') el.addClass('transfer-valor-transferido--negativo');
      else el.addClass('transfer-valor-transferido--zero');
    }

    private atualizarTax() {
      var precoVenda = parseFloat(this.elInputPreco.val() as string) || 0;
      var perdaRaw = parseFloat(this.elInputPerdaVenda.val() as string);
      var perdaPct = isNaN(perdaRaw) ? FSW_PERDA_VENDA_PADRAO : this.clampPerdaVendaPct(perdaRaw);
      var pm = this.precoMercadoSelecionado;
      if (pm > 0) this.elPrecoMercado.text(pm.toLocaleString('pt-BR'));
      else this.elPrecoMercado.text('—');

      if (pm > 0) {
        var binSug = this.binSugeridoPorRef(pm, perdaPct);
        this.elBinSugerido.text(binSug > 0 ? binSug.toLocaleString('pt-BR') : '—');
        var taxRef = Math.floor(pm * FSW_TAXA_REF_MERCADO);
        this.elTaxRefMercado.text(taxRef.toLocaleString('pt-BR'));
      } else {
        this.elBinSugerido.text('—');
        this.elTaxRefMercado.text('—');
      }

      if (precoVenda > 0) {
        var br = this.obterBreakdownCalculoTaxa(pm, precoVenda, perdaRaw);
        this.elTaxValor.text(br.taxa_ea_venda_sobre_bin.toLocaleString('pt-BR'));
        this.elTaxRecebe.text(br.liquido_conta_a_apos_venda.toLocaleString('pt-BR'));
        var custoFsw = this.custoFswTransferEur(br.coins_registo);
        this.elFswCustoEur.text(custoFsw > 0 ? this.formatarEurPt(custoFsw) : (this.getTaxaFswEurPor100k() <= 0 ? '—' : this.formatarEurPt(0)));
        if (pm > 0) {
          var vl = br.valor_transferido_liquido;
          this.elValorTransferido.text(vl.toLocaleString('pt-BR'));
          if (vl > 0) this.aplicarEstiloValorTransferido('positivo');
          else if (vl < 0) this.aplicarEstiloValorTransferido('negativo');
          else this.aplicarEstiloValorTransferido('zero');
        } else {
          this.elValorTransferido.text('—');
          this.aplicarEstiloValorTransferido('vazio');
        }
      } else {
        this.elTaxValor.text('—');
        this.elTaxRecebe.text('—');
        this.elFswCustoEur.text('—');
        this.elValorTransferido.text('—');
        this.aplicarEstiloValorTransferido('vazio');
      }
    }

    private adicionarLog(msg: string, tipo: 'info' | 'sucesso' | 'erro' = 'info') {
      var cls = tipo === 'sucesso' ? 'transfer-log-line--sucesso' : (tipo === 'erro' ? 'transfer-log-line--erro' : 'transfer-log-line--info');
      var hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      var msgSeguro = this.escaparHtmlTransfer(this.normalizarTextoTransfer(msg));
      var linha = '<div class="' + cls + '" style="margin-bottom:2px;">[' + hora + '] ' + msgSeguro + '</div>';
      this.elLogLines.append(linha);
      var container = document.getElementById('transferLogContainer');
      if (container) container.scrollTop = container.scrollHeight;
    }

    // ─── Reset ────────────────────────────────────────────────────────────────

    private reset(silencioso: boolean = false) {
      if (!silencioso && (this.contaAPronta || this.contaBPronta)) {
        var contaAId = this.getIdContaAPlugin();
        var contaBId = this.getIdContaBPlugin();
        if (contaAId || contaBId) {
          this.comunicacao.enviar(Util.ACAO_TRANSFER_FECHAR_CONTAS, { contaAId, contaBId }, () => {});
        }
      }

      this.pararAtualizacaoLatencia();
      this.pararHeartbeat();
      this.contaAPronta = false;
      this.contaBPronta = false;
      this.executando = false;
      this.precoMercadoSelecionado = 0;
      this.elSelectJogador.html('<option value="">— prepare a conta vendedora (A) primeiro —</option>');
      this.elInputPreco.val('');
      this.elPrecoMercado.text('—');
      this.elInputPerdaVenda.val(String(FSW_PERDA_VENDA_PADRAO));
      this.elBinSugerido.text('—');
      this.elTaxValor.text('—');
      this.elTaxRefMercado.text('—');
      this.elTaxRecebe.text('—');
      this.elValorTransferido.text('—');
      this.aplicarEstiloValorTransferido('vazio');
      this.elFswCustoEur.text('—');
      this.elLatency.text('—').removeClass('text-danger text-success');
      this.elSessionInfo.text('—');
      this.elLogLines.empty();
      this.limparAutoUi();
      this.modoTransferSelecionado = 'manual';
      this.elManualFooter.show();
      this.elBtnPrepararA.show();
      this.elBtnPrepararB.show();
      this.atualizarStatusBadge(this.elStatusA, 'INATIVA', null);
      this.atualizarStatusBadge(this.elStatusB, 'INATIVA', null);
      this.elBtnPrepararA.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
      this.elBtnPrepararB.prop('disabled', false).html('<i class="fas fa-plug mr-1"></i> Preparar conta');
      this.elBtnExecutar.prop('disabled', false).html('<i class="fas fa-exchange-alt mr-1"></i>Transferir');
      this.atualizarGeralStatus('Aguardando', 'info');
      this.elAForm.hide();
      this.elBForm.hide();
      this.elADropdown.hide();
      this.elBDropdown.hide();
      if (this.contaA || this.contaB || this.contaATemp || this.contaBTemp) this.renderizarContas();
      this.sincronizarAbaTransferAtual();
    }
  }
}
